import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import {MAIN_URL } from "../../../Configurations/Urls";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

function ForgotPassword({
  title = "Forgot Password",
  appName = "HR ERP",
  description,
}) {
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const sendOtp = async (e) => {
    e.preventDefault();
    let formErrors = {};
    setErrors({});

    if (!email) {
      formErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
    }

    if (Object.keys(formErrors)?.length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${MAIN_URL}/api/auth/forgot-password`, { email },  { headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },});
      toast.success(res.data.message);
      setOtpSent(true);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to send OTP. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    let formErrors = {};
    setErrors({});

    if (!enteredOtp) {
      formErrors.otp = "OTP is required";
    } else if (!/^[0-9]{6}$/.test(enteredOtp)) {
      formErrors.otp = "Invalid OTP format";
    }

    if (Object.keys(formErrors)?.length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${MAIN_URL}/api/auth/verified-otp`, {
        email,
        otp: +enteredOtp,
      });
      toast.success(res.data.message || "OTP verified!");
      navigate("/reset-password", { state: { email } });
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to verify OTP. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ems-auth-root">

      <div className="ems-auth-left">
        <div className="ems-auth-card">

          <div className="ems-auth-brand">
            <img src={logo} alt="HR ERP" className="ems-auth-brand-logo" />
          </div>

          <h1 className="ems-auth-h">{title}</h1>
          <p className="ems-auth-sub">
            {!otpSent
              ? "Enter your email and we’ll send you a one-time password."
              : "Enter the OTP we sent to your email to continue."}
          </p>

          {!otpSent ? (
            <form onSubmit={sendOtp} noValidate>
              <div className="ems-auth-field">
                <label htmlFor="forgot-email">Email Address</label>
                <input
                  id="forgot-email"
                  placeholder="name@company.com"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                  className="ems-fm-input"
                />
                {errors.email && <span className="ems-fm-err">{errors.email}</span>}
              </div>
              <div className="ems-auth-actions">
                <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                  {loading ? <><SpinSVG /> Sending…</> : "Send OTP"}
                </button>
                <button
                  type="button"
                  className="ems-btn"
                  onClick={() => navigate("/login")}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={verifyOtp} noValidate>
              <div className="ems-auth-field">
                <label htmlFor="forgot-otp">OTP</label>
                <input
                  id="forgot-otp"
                  placeholder="Enter 6-digit OTP"
                  type="text"
                  value={enteredOtp}
                  onChange={(e) => { setEnteredOtp(e.target.value); setErrors((p) => ({ ...p, otp: "" })); }}
                  className="ems-fm-input"
                />
                {errors.otp && <span className="ems-fm-err">{errors.otp}</span>}
              </div>
              <div className="ems-auth-actions">
                <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                  {loading ? <><SpinSVG /> Verifying…</> : "Verify OTP"}
                </button>
                <button
                  type="button"
                  className="ems-btn"
                  onClick={() => { setOtpSent(false); setEnteredOtp(""); setErrors({}); }}
                  disabled={loading}
                >
                  Change Email
                </button>
              </div>
            </form>
          )}

        </div>
      </div>

      <div className="ems-auth-right">
        <div className="ems-auth-hero">
          <div className="ems-auth-hero-mark">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path d="M5 8h16M5 13h10M5 18h16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="ems-auth-hero-h">{appName}</h2>
          <p className="ems-auth-hero-p">
            {description ||
              "A powerful and flexible solution to manage your system efficiently. From user access to real-time reporting, everything is centralized and streamlined for a better experience."}
          </p>
          <div className="ems-auth-trust">
            <span className="ems-auth-trust-dot" />
            Trusted by thousands of users to manage their workflows.
          </div>
        </div>
        <p className="ems-auth-copy">&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </div>

    </div>
  );
}

export default ForgotPassword;
